/**
 * @file chunk-errors — patrones canónicos de errores producidos por
 * SPA + lazy chunks cuando hay un deploy reciente con hashes nuevos
 * y el browser tiene HTML/cache con referencias al hash viejo.
 *
 * Único origen de verdad: tanto `main.tsx` (auto-reload listener),
 * `ErrorView` (fallback del ErrorBoundary) y `sentry.ts` (filter
 * BeforeSend) lo consumen. Si una variante de error escapa de algún
 * lado, se agrega acá una vez y los 3 lugares se enteran.
 *
 * Origen de cada patrón observado en producción (audit Sentry inbox):
 *
 *   - `Failed to fetch dynamically imported module` — Vite nativo
 *     cuando dynamic `import()` retorna 404 o MIME inesperado.
 *   - `Importing a module script failed` — variante moderna del
 *     navegador cuando el script src 404ea.
 *   - `Loading chunk N failed` / `ChunkLoadError` — webpack legacy.
 *   - `error loading dynamically imported module` — variante alterna.
 *   - `Unable to preload CSS for …` — el `<link rel="modulepreload">`
 *     de un chunk viejo apunta a un CSS hash inexistente.
 *   - `'text/html' is not a valid JavaScript MIME type` — DO App
 *     Platform sirve `index.html` (200 text/html) como fallback de
 *     SPA cuando el JS chunk con hash viejo no existe; el browser
 *     intenta evaluarlo como JS.
 */

const CHUNK_PATTERNS: readonly RegExp[] = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /Loading chunk\b/i,
  /ChunkLoadError/i,
  /error loading dynamically imported module/i,
  /Unable to preload CSS for/i,
  /'text\/html' is not a valid JavaScript MIME type/i,
];

/** Extrae un string testeable desde cualquier shape de error. */
export function errorMessageOf(err: unknown): string {
  if (err instanceof Error) {
    return `${err.message} ${err.name ?? ""}`;
  }
  if (typeof err === "string") {
    return err;
  }
  // ErrorEvent.message, PromiseRejectionEvent.reason crudo, etc.
  if (err && typeof err === "object" && "message" in err) {
    const m = (err as { message?: unknown }).message;
    if (typeof m === "string") return m;
  }
  return "";
}

/**
 * isStaleChunkError — true si el error parece causado por un deploy
 * que renovó hashes de los chunks. La recuperación es siempre
 * `location.reload()` (cache busting).
 */
export function isStaleChunkError(err: unknown): boolean {
  const msg = errorMessageOf(err);
  if (!msg) return false;
  return CHUNK_PATTERNS.some((re) => re.test(msg));
}

/**
 * Flag de sessionStorage: una sola recarga por sesión ante stale-chunk,
 * para no caer en loop si el bundle nuevo también está roto. El timer de
 * main.tsx lo limpia tras un boot exitoso. Único origen de verdad,
 * compartido por main.tsx, ErrorView y el lazyRoute de app.tsx.
 */
export const CHUNK_RELOAD_FLAG = "omnipos:chunk-reload-attempted";

/**
 * reloadOnceForStaleChunk — si `err` es un stale-chunk, dispara UNA recarga
 * por sesión (cache busting hacia el index.html nuevo) y devuelve true.
 * Si no es stale-chunk devuelve false (el caller decide). Si ya se intentó
 * recargar esta sesión, devuelve true sin recargar de nuevo (evita loop).
 *
 * Esto centraliza la recuperación que antes vivía duplicada y, sobre todo,
 * permite invocarla desde el factory de cada lazy route (donde el import()
 * falla), porque un fallo de import lazy lo captura React como error de
 * render — NO dispara `unhandledrejection`, así que el listener global de
 * main.tsx nunca se enteraba de los fallos de chunk durante la navegación.
 */
export function reloadOnceForStaleChunk(err: unknown): boolean {
  if (!isStaleChunkError(err)) return false;
  try {
    if (globalThis.sessionStorage?.getItem(CHUNK_RELOAD_FLAG)) return true;
    globalThis.sessionStorage?.setItem(CHUNK_RELOAD_FLAG, "1");
  } catch {
    // sessionStorage indisponible (modo privado estricto): recargamos igual.
  }
  globalThis.location.reload();
  return true;
}
